<template>
  <div>
    <div class="d-flex align-center justify-center py-8 bg-default">
      <v-container>
        <div class="text-center">
          <h4 class="secondary-font">Agendar Exame</h4>
          <h5 class="secondary-font text--secondary mt-2">
            Confirme seus dados, selecione seu horário com o médico e faça o agendamento do seu exame.
          </h5>
        </div>
      </v-container>
    </div>
    <v-container class="mt-5">
      <v-row no-gutters justify="center">
        <v-col cols="12" sm="10" md="8" lg="6" xl="5">
          <v-form ref="form">
            <h4 class="secondary-font">Selecione o tipo do seu exame:</h4>
            <v-select
              class="mt-5"
              v-model="exame"
              :items="exames"
              outlined
              :disabled="!!idAso"
              label="Exame"
              :rules="[v => !!v || 'Campo obrigatório']"
              dense
            ></v-select>

            <h4 class="secondary-font">
              Selecione o dia, o horário (de Brasília) e o(a) médico(a) para o atendimento:
            </h4>

            <HorariosSlider @update-data="buscaHorarios" class="mt-5 mb-7" />
            <v-alert v-if="horarioInfo" type="info" class="mt-5">
              Você já possui um horário marcado às
              {{ horarioInfo.horario.toLocaleTimeString('pt-br', { hour: '2-digit', minute: '2-digit' }) }} do dia
              {{ horarioInfo.horario.toLocaleDateString('pt-br') }} com o(a) médico(a) {{ horarioInfo.medico }}. Para
              alterar basta escolher outro horário da sua preferência
            </v-alert>
            <v-card outlined class="bg-default my-5" v-for="medico in medicos" :key="medico.id">
              <v-card-title class="primary--text secondary-font font-weight-bold">{{ medico.nome }}</v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row no-gutters justify="start">
                  <v-btn
                    v-for="horario in medico.horarios"
                    :key="horario.id"
                    depressed
                    rounded
                    :outlined="selectedHorario !== horario.id"
                    color="primary font-weight-bold"
                    class="ma-1 secondary-font"
                    @click="selectedHorario = horario.id"
                    >{{ horario.horario }}</v-btn
                  >
                </v-row>
              </v-card-text>
            </v-card>
            <v-alert v-if="!medicos.length" type="warning" class="mt-5"
              >Não existem horários disponíveis na data escolhida.</v-alert
            >
            <v-row v-else no-gutters justify="center" align="center" class="mt-10">
              <v-btn
                @click="submit"
                color="primary"
                class="secondary-font text-capitalize font-weight-bold py-7"
                elevation="24"
                rounded
                x-large
              >
                Agendar Exame
                <v-spacer></v-spacer>
                <v-icon right>mdi-arrow-right-bold-circle-outline</v-icon>
              </v-btn>
            </v-row>
            <v-row no-gutters justify="center" align="center" class="mt-7 mb-8">
              <v-btn
                to="/agendamentos"
                color="error"
                class="secondary-font text-capitalize font-weight-black"
                outlined
                rounded
              >
                Cancelar
                <v-icon right>mdi-close</v-icon>
              </v-btn>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import fichaClinicaService from '@/services/fichaClinica'
import HorariosSlider from '@/modules/AcessoRapido/components/HorariosSlider'

export default {
  props: {
    idAso: {
      required: false
    },
    dtExame: {
      required: false
    }
  },
  components: {
    HorariosSlider
  },
  data: () => ({
    exames: [
      { value: 1, text: 'Admissional' },
      { value: 2, text: 'Demissional' },
      { value: 3, text: 'Periódico' },
      { value: 4, text: 'Mudança de Função' },
      { value: 5, text: 'Retorno ao Trabalho' }
    ],
    exame: 1,
    selectedHorario: null,
    medicos: [],
    horarioInfo: null
  }),
  methods: {
    ...mapActions('fichaClinica', ['preparaFichaClinica']),

    async buscaHorarios(dataSelecionada = new Date().toISOString().slice(0, 10)) {
      this.$showLoading()
      this.selectedHorario = null

      try {
        const { data } = await fichaClinicaService.buscaHorarios(dataSelecionada)
        this.medicos = data
      } catch (error) {
        console.error(error)

        if (error.response) {
          this.$showErrorMessage(error.response.data.message)
        }
      } finally {
        this.$hideLoading()
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return

      if (!this.selectedHorario) {
        return this.$showErrorMessage('Selecione um horário')
      }

      this.$showLoading()
      try {
        if (this.idAso) {
          await fichaClinicaService.updateHorarioAgendamento({
            newIdHorario: this.selectedHorario,
            idAso: this.idAso
          })
          this.$showSuccessMessage('Horário atualizado com sucesso')
          this.$router.push({ path: '/agendamentos' })
        } else {
          const { data } = await fichaClinicaService.salvarAgendamento({
            idExame: this.exame,
            idHorario: this.selectedHorario
          })
          this.preencherFichaClinica(data.aso)
        }
      } catch ({ response }) {
        this.$showErrorMessage(response.data.message)
      } finally {
        this.$hideLoading()
      }
    },
    async preencherFichaClinica(aso) {
      this.$showLoading()
      const success = await this.preparaFichaClinica(aso)
      this.$hideLoading()

      if (success) this.$router.push({ name: 'Ficha Clínica' })
    },
    async getHorarioInfo() {
      this.$showLoading()
      try {
        const { data } = await fichaClinicaService.getHorarioInfo(parseInt(this.idAso))
        this.horarioInfo = data
        this.horarioInfo.horario = new Date(data.horario)
      } catch ({ response }) {
        this.$showErrorMessage(response.data.message)
      } finally {
        this.$hideLoading()
      }
    }
  },
  created() {
    this.buscaHorarios()
  },
  mounted() {
    if (this.idAso) {
      this.getHorarioInfo()
    }
  }
}
</script>
